import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Loader2, ChevronDown } from 'lucide-react';
import { useJob, useJobLogs, useJobAssignments, useJobCallbackLogs, useJobProviderAttempts } from '@/hooks/useJobs';
import InfoItem from '@/components/jobs/InfoItem';
import JobStatusBadge from '@/components/jobs/JobStatusBadge';
import LogViewer from '@/components/jobs/LogViewer';
import ImageGallery from '@/components/jobs/ImageGallery';
import ZipImageViewer from '@/components/jobs/ZipImageViewer';
import CallbackUrlInfo from '@/components/jobs/CallbackUrlInfo';
import CallbackLogsSection from '@/components/jobs/CallbackLogsSection';
import ProviderAttemptsToggle from '@/components/jobs/ProviderAttemptsToggle';
import ProviderAttemptsSection from '@/components/jobs/ProviderAttemptsSection';
import { downloadJobData, type DownloadProgress } from '@/lib/downloadJobData';
import { extractJobImages, hasZipExtension } from '@/lib/imageUtils';
import { formatDuration } from '@/lib/utils';

export default function JobDetailPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();

  const { data: job, isLoading: jobLoading } = useJob(jobId!);
  const { data: logsData } = useJobLogs(jobId!);
  const { data: assignmentsData } = useJobAssignments(jobId!);

  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [jobImages, setJobImages] = useState<{ input?: string; output: string[] } | null>(null);
  const [showCallbackLogs, setShowCallbackLogs] = useState(false);
  const [showProviderAttempts, setShowProviderAttempts] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: callbackLogs, isLoading: callbackLogsLoading } = useJobCallbackLogs(jobId!, showCallbackLogs);
  const { data: providerAttempts, isLoading: providerAttemptsLoading } = useJobProviderAttempts(
    jobId!,
    showProviderAttempts && job?.type_name === 'model-run',
  );

  const assignment = assignmentsData?.items?.[0];
  const logs = logsData?.items || [];

  // Extract images when job loads
  useEffect(() => {
    if (job) {
      extractJobImages(job).then((images) => {
        setJobImages(images);
      });
    }
  }, [job]);

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  if (!jobId) {
    return (
      <div className="p-8">
        <div className="glass-card rounded-lg p-12">
          <div className="flex flex-col items-center justify-center gap-4">
            <div className="text-muted-foreground text-lg">Invalid job ID</div>
            <button
              type="button"
              onClick={() => navigate('/jobs')}
              className="px-4 py-2 rounded-md bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 transition-colors"
            >
              Back to Jobs List
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleDownload = async (includeFiles: boolean) => {
    if (!job) return;

    setIsDropdownOpen(false);
    setIsDownloading(true);
    setDownloadProgress(null);
    try {
      await downloadJobData(job, logs, assignmentsData?.items || [], includeFiles, (progress) => {
        setDownloadProgress(progress);
      });
    } catch (error) {
      console.error('Download failed:', error);
      alert(`Failed to download job data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsDownloading(false);
      setDownloadProgress(null);
    }
  };

  return (
    <div className="p-8 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate('/jobs')}
            className="p-2 rounded-md hover:bg-muted transition-colors"
            title="Back to Jobs"
            aria-label="Back to Jobs"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold">Job Details</h1>
            <p className="text-sm text-muted-foreground mt-1">Complete job information and logs</p>
          </div>
          {job && <JobStatusBadge status={job.status_name} />}
        </div>
        {job && (
          <div ref={dropdownRef} className="relative">
            <button
              type="button"
              onClick={() => !isDownloading && setIsDropdownOpen(!isDropdownOpen)}
              disabled={isDownloading}
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Download job data as ZIP"
            >
              {isDownloading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              <span>
                {isDownloading && downloadProgress ? (
                  <>
                    {downloadProgress.phase === 'preparing' && 'Preparing...'}
                    {downloadProgress.phase === 'downloading' && (
                      <>
                        Downloading {downloadProgress.filesDownloaded}/{downloadProgress.totalFiles}
                        {downloadProgress.currentFile && `: ${downloadProgress.currentFile}`}
                      </>
                    )}
                    {downloadProgress.phase === 'packaging' && 'Creating ZIP...'}
                  </>
                ) : isDownloading ? (
                  'Downloading...'
                ) : (
                  'Download'
                )}
              </span>
              {!isDownloading && <ChevronDown className="h-4 w-4" />}
            </button>

            {isDropdownOpen && !isDownloading && (
              <div className="absolute right-0 mt-2 w-64 bg-background rounded-lg border border-border shadow-lg overflow-hidden z-10">
                <button
                  type="button"
                  onClick={() => handleDownload(false)}
                  className="w-full px-4 py-3 text-left hover:bg-muted transition-colors border-b border-border"
                >
                  <div className="font-medium text-sm">Download Metadata Only</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    JSON files and logs (fast, small)
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => handleDownload(true)}
                  className="w-full px-4 py-3 text-left hover:bg-muted transition-colors"
                >
                  <div className="font-medium text-sm flex items-center gap-2">
                    Download All Files
                    <span className="text-yellow-500">⚠️</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Includes input/output files (potentially large)
                  </div>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      {jobLoading ? (
        <div className="glass-card rounded-lg p-12">
          <div className="flex items-center justify-center">
            <div className="text-muted-foreground">Loading job details...</div>
          </div>
        </div>
      ) : job ? (
        <div className="space-y-6">
          {/* Job Overview */}
          <div className="glass-card rounded-lg p-6 space-y-4">
            <h2 className="text-xl font-semibold">Overview</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <InfoItem label="Job ID" value={job.id} monospace />
              <InfoItem label="Type" value={job.type_name} />
              <InfoItem label="Priority" value={job.priority.toString()} />
              <InfoItem label="Created" value={job.create_ts} monospace />
              <InfoItem label="Started" value={job.start_ts || "—"} monospace />
              <InfoItem label="Completed" value={job.complete_ts || "—"} monospace />
              <InfoItem label="Duration" value={job.start_ts ? formatDuration(job.start_ts, job.complete_ts) : "—"} />
              <InfoItem label="Attempt" value={job.attempt_number.toString()} />
              <InfoItem label="Retries" value={`${job.retry_count} / ${job.max_retries}`} />
              <InfoItem label="Callback URL" monospace>
                {job.callback_url ? (
                  <CallbackUrlInfo
                    callbackUrl={job.callback_url}
                    showCallbackLogs={showCallbackLogs}
                    onToggleLogs={() => setShowCallbackLogs(!showCallbackLogs)}
                  />
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </InfoItem>

              {/* Debug toggle — only for jobs that proxy to external providers (model-run). */}
              {job.type_name === 'model-run' && (
                <div className="lg:col-span-3 md:col-span-2 flex flex-wrap gap-2 pt-2">
                  <ProviderAttemptsToggle
                    show={showProviderAttempts}
                    onToggle={() => setShowProviderAttempts(!showProviderAttempts)}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Callback Delivery Logs — own top-level card when toggled on */}
          {showCallbackLogs && job.callback_url && (
            <div className="glass-card rounded-lg p-6 space-y-4">
              <h2 className="text-xl font-semibold">Callback Delivery Logs</h2>
              <CallbackLogsSection callbackLogs={callbackLogs} isLoading={callbackLogsLoading} />
            </div>
          )}

          {/* Provider Attempts — own top-level card when toggled on */}
          {showProviderAttempts && job.type_name === 'model-run' && (
            <div className="glass-card rounded-lg p-6 space-y-4">
              <h2 className="text-xl font-semibold">Provider Attempts</h2>
              <ProviderAttemptsSection attempts={providerAttempts} isLoading={providerAttemptsLoading} />
            </div>
          )}

          {/* Worker Assignment */}
          <div className="glass-card rounded-lg p-6 space-y-4">
            <h2 className="text-xl font-semibold">Worker Assignment</h2>
            {assignment ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <InfoItem label="Worker ID" value={assignment.worker_id} monospace />
                <InfoItem label="Claimed" value={assignment.create_ts || "—"} monospace />
                <InfoItem label="Last Active" value={assignment.heartbeat_ts || "—"} monospace />
                <InfoItem label="Released" value={assignment.release_ts || "—"} monospace />
              </div>
            ) : (
              <div className="text-muted-foreground text-sm">No worker assigned yet</div>
            )}
          </div>

          {/* Payload */}
          <div className="glass-card rounded-lg p-6 space-y-4">
            <h2 className="text-xl font-semibold">Payload</h2>
            <div className="bg-muted/50 rounded-lg p-4 overflow-x-auto">
              <pre className="text-sm font-mono text-muted-foreground">{JSON.stringify(job.payload, null, 2)}</pre>
            </div>
          </div>

          {/* Input Image or ZIP */}
          {job.payload?.input_file && typeof job.payload.input_file === 'string' && (
            <>
              {/* Check if input_file is a ZIP */}
              {hasZipExtension(job.payload.input_file) ? (
                <ZipImageViewer
                  zipUrl={job.payload.input_file}
                  title="Input Images (from ZIP)"
                  compact={false}
                />
              ) : (
                /* Regular image */
                jobImages?.input && (
                  <ImageGallery
                    images={[jobImages.input]}
                    title="Input Image"
                    compact={false}
                  />
                )
              )}
            </>
          )}

          {/* Result (if completed) */}
          {job.result && (
            <div className="glass-card rounded-lg p-6 space-y-4">
              <h2 className="text-xl font-semibold">Result</h2>
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 overflow-x-auto">
                <pre className="text-sm font-mono text-green-400">{JSON.stringify(job.result, null, 2)}</pre>
              </div>
            </div>
          )}

          {/* Output Images */}
          {jobImages?.output && jobImages.output.length > 0 && (
            <ImageGallery
              images={jobImages.output}
              title={`Output Images (${jobImages.output.length})`}
              compact={false}
            />
          )}

          {/* Error (if failed) */}
          {job.error && (
            <div className="glass-card rounded-lg p-6 space-y-4">
              <h2 className="text-xl font-semibold">Error</h2>
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 overflow-x-auto">
                <pre className="text-sm font-mono text-red-400">{JSON.stringify(job.error, null, 2)}</pre>
              </div>
            </div>
          )}

          {/* Logs */}
          <div className="glass-card rounded-lg p-6 space-y-4">
            <h2 className="text-xl font-semibold">Logs</h2>
            <LogViewer logs={logs} autoScroll={false} />
          </div>
        </div>
      ) : (
        <div className="glass-card rounded-lg p-12">
          <div className="flex flex-col items-center justify-center gap-4">
            <div className="text-muted-foreground text-lg">Job not found</div>
            <button
              type="button"
              onClick={() => navigate('/jobs')}
              className="px-4 py-2 rounded-md bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 transition-colors"
            >
              Back to Jobs List
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
